/**
 * useClaimants — CRUD + pagination + filter state for the claimants list.
 * Blueprint: CMM Section B endpoints — GET/POST/PUT /claimants
 *
 * MOCK DATA: Seeded from Paluch LLC VGA Cases 1.30.2025 for UI development.
 * All SSN, DOB, email, phone values are synthetic — real names/states only.
 *
 * TODO: Replace MOCK_CLAIMANTS with real API calls via your existing useApi hook.
 * TODO: Firm-scoping middleware is enforced server-side (F-14).
 * TODO: F-08 duplicate check — on create, handle 409 Conflict (hard block).
 */
import { useState, useCallback, useEffect } from 'react';
import type {
  Claimant, ClaimantFilters, TortCode
} from '@/types/cmm/claimant.types';

// ── State abbreviation map ───────────────────────────────────
const STATE_ABBR: Record<string, string> = {
  'New York': 'NY', 'Washington': 'WA', 'California': 'CA',
  'New Mexico': 'NM', 'Illinois': 'IL', 'West Virginia': 'WV',
  'Georgia': 'GA', 'Texas': 'TX', 'Florida': 'FL', 'Ohio': 'OH',
};

// ── Mock claimants — VGA tort, sourced from Paluch LLC CSV ───
const MOCK_CLAIMANTS: Claimant[] = [
  {
    id: '3510738-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Jessica',
    lastName: 'Rupe',
    dob: '2009-03-15',
    ssnLast4: '4821',
    email: 'jessica.rupe@placeholder.dev',
    phonePrimary: '+13155550101',
    addressRaw: '412 Maple Ave, Cortland, NY 13045',
    addressValidated: {
      deliveryLine1: '412 Maple Ave',
      lastLine: 'Cortland NY 13045',
      city: 'Cortland',
      state: 'NY',
      zip5: '13045',
      dpvMatchCode: 'Y',
    },
    residencyStartDate: '2022-01-01',
    state: 'NY',
    pipelineStage: 'DOC_COLLECTION',
    overlapPriorityTier: 1,
    estPortfolioValueRange: '$45K-$90K',
    socialMediaPlatforms: ['Facebook', 'Roblox', 'Fortnite'],
    gamingHoursPerDay: 5,
    crossLitigationFlag: false,
    createdAt: '2024-11-22T16:36:00Z',
    updatedAt: '2024-11-22T16:36:00Z',
  },
  {
    id: '3511035-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Joshua',
    lastName: 'Cork',
    dob: '2013-06-20',
    ssnLast4: '7734',
    email: 'joshua.cork@placeholder.dev',
    phonePrimary: '+15095550188',
    addressRaw: '881 Pine St, Spokane, WA 99207',
    addressValidated: {
      deliveryLine1: '881 Pine St',
      lastLine: 'Spokane WA 99207',
      city: 'Spokane',
      state: 'WA',
      zip5: '99207',
      dpvMatchCode: 'Y',
    },
    residencyStartDate: '2021-08-01',
    state: 'WA',
    pipelineStage: 'SUBSTANTIATED',
    overlapPriorityTier: 2,
    estPortfolioValueRange: '$30K-$60K',
    socialMediaPlatforms: ['Roblox', 'Minecraft', 'Fortnite'],
    gamingHoursPerDay: 6,
    crossLitigationFlag: false,
    createdAt: '2024-11-28T14:48:00Z',
    updatedAt: '2024-11-28T14:48:00Z',
  },
  {
    id: '3455940-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Sam',
    lastName: 'Tsou',
    dob: '2000-04-10',
    ssnLast4: '2295',
    email: 'sam.tsou@placeholder.dev',
    phonePrimary: '+16265550244',
    addressRaw: '54 Valley Blvd, El Monte, CA 91731',
    addressValidated: {
      deliveryLine1: '54 Valley Blvd',
      lastLine: 'El Monte CA 91731',
      city: 'El Monte',
      state: 'CA',
      zip5: '91731',
      dpvMatchCode: 'Y',
    },
    residencyStartDate: '2020-03-15',
    state: 'CA',
    pipelineStage: 'REVIEW',
    overlapPriorityTier: 1,
    estPortfolioValueRange: '$60K-$120K',
    socialMediaPlatforms: ['Instagram', 'TikTok', 'Facebook'],
    gamingHoursPerDay: 8,
    crossLitigationFlag: true,
    crossLitigationMatchIds: ['3519226-vga'],
    createdAt: '2024-11-14T01:52:00Z',
    updatedAt: '2024-11-14T01:52:00Z',
  },
  {
    id: '3519226-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Edward',
    lastName: 'Dean',
    dob: '1984-09-05',
    ssnLast4: '6612',
    email: 'edward.dean@placeholder.dev',
    phonePrimary: '+15755550377',
    addressRaw: '29 Cactus Rd, Hobbs, NM 88240',
    addressValidated: {
      deliveryLine1: '29 Cactus Rd',
      lastLine: 'Hobbs NM 88240',
      city: 'Hobbs',
      state: 'NM',
      zip5: '88240',
      dpvMatchCode: 'Y',
    },
    residencyStartDate: '2019-06-01',
    state: 'NM',
    pipelineStage: 'REVIEW',
    overlapPriorityTier: 2,
    estPortfolioValueRange: '$40K-$80K',
    socialMediaPlatforms: ['Facebook', 'Twitter'],
    gamingHoursPerDay: 8,
    crossLitigationFlag: true,
    crossLitigationMatchIds: ['3455940-vga'],
    createdAt: '2024-11-23T02:06:00Z',
    updatedAt: '2024-11-23T02:06:00Z',
  },
  {
    id: '3507066-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Joshua',
    lastName: 'Monterrosa',
    dob: '2001-11-18',
    ssnLast4: '9901',
    email: 'joshua.monterrosa@placeholder.dev',
    phonePrimary: '+15105550512',
    addressRaw: '1200 Garrard Ave, Richmond, CA 94801',
    addressValidated: {
      deliveryLine1: '1200 Garrard Ave',
      lastLine: 'Richmond CA 94801',
      city: 'Richmond',
      state: 'CA',
      zip5: '94801',
      dpvMatchCode: 'Y',
    },
    residencyStartDate: '2020-09-01',
    state: 'CA',
    pipelineStage: 'DOC_COLLECTION',
    overlapPriorityTier: 3,
    estPortfolioValueRange: '$35K-$70K',
    socialMediaPlatforms: ['Instagram', 'Fortnite'],
    gamingHoursPerDay: 6,
    crossLitigationFlag: false,
    createdAt: '2024-12-05T09:32:00Z',
    updatedAt: '2024-12-05T09:32:00Z',
  },
  {
    id: '3516024-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Bartosz',
    lastName: 'Rapciak',
    dob: '2005-02-27',
    ssnLast4: '3348',
    email: 'bartosz.rapciak@placeholder.dev',
    phonePrimary: '+17085550629',
    addressRaw: '7741 Oak Park Ave, Norridge, IL 60706',
    addressValidated: {
      deliveryLine1: '7741 Oak Park Ave',
      lastLine: 'Norridge IL 60706',
      city: 'Norridge',
      state: 'IL',
      zip5: '60706',
      dpvMatchCode: 'S',
    },
    residencyStartDate: '2021-01-15',
    state: 'IL',
    pipelineStage: 'DEFICIENT',
    overlapPriorityTier: 3,
    estPortfolioValueRange: '$25K-$50K',
    socialMediaPlatforms: ['Minecraft', 'Fortnite'],
    gamingHoursPerDay: 4,
    crossLitigationFlag: false,
    createdAt: '2024-11-22T20:54:00Z',
    updatedAt: '2024-11-22T20:54:00Z',
  },
  {
    id: '3458717-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Steven',
    lastName: 'Kelly',
    dob: '1994-07-04',
    ssnLast4: '1156',
    email: 'steven.kelly@placeholder.dev',
    phonePrimary: '+13045550741',
    addressRaw: '311 5th Ave, Huntington, WV 25703',
    addressValidated: {
      deliveryLine1: '311 5th Ave',
      lastLine: 'Huntington WV 25703',
      city: 'Huntington',
      state: 'WV',
      zip5: '25703',
      dpvMatchCode: 'Y',
    },
    residencyStartDate: '2018-04-01',
    state: 'WV',
    pipelineStage: 'AWARD_READY',
    overlapPriorityTier: 1,
    estPortfolioValueRange: '$55K-$110K',
    socialMediaPlatforms: ['Call of Duty', 'GTA 5'],
    gamingHoursPerDay: 7,
    crossLitigationFlag: false,
    createdAt: '2024-11-14T07:02:00Z',
    updatedAt: '2024-11-14T07:02:00Z',
  },
  {
    id: '3559439-vga',
    tortCode: 'VGA',
    sourceFirmId: 'paluch-llc',
    firstName: 'Brandon',
    lastName: 'Mitchell',
    dob: '1999-05-30',
    ssnLast4: '8823',
    email: 'brandon.mitchell@placeholder.dev',
    phonePrimary: '+17705550855',
    addressRaw: '450 Shiloh Rd, Kennesaw, GA 30066',
    addressValidated: {
      deliveryLine1: '450 Shiloh Rd',
      lastLine: 'Kennesaw GA 30066',
      city: 'Kennesaw',
      state: 'GA',
      zip5: '30066',
      dpvMatchCode: 'Y',
    },
    residencyStartDate: '2022-07-01',
    state: 'GA',
    pipelineStage: 'INTAKE',
    overlapPriorityTier: 2,
    estPortfolioValueRange: '$30K-$65K',
    socialMediaPlatforms: ['Call of Duty', 'Fortnite'],
    gamingHoursPerDay: 5,
    crossLitigationFlag: false,
    createdAt: '2024-12-09T23:45:00Z',
    updatedAt: '2024-12-09T23:45:00Z',
  },
];

interface UseClaimantsReturn {
  claimants:   Claimant[];
  total:       number;
  isLoading:   boolean;
  error:       string | null;
  filters:     ClaimantFilters;
  setFilters:  (f: Partial<ClaimantFilters>) => void;
  fetchClaimants:     () => Promise<void>;
  createClaimant:     (data: Partial<Claimant>) => Promise<Claimant | null>;
  updateClaimant:     (id: string, data: Partial<Claimant>) => Promise<Claimant | null>;
  advanceStage:       (id: string, newStage: string) => Promise<void>;
  acknowledgeOverlap: (id: string) => Promise<void>;
}

export function useClaimants(tortCode?: TortCode): UseClaimantsReturn {
  const [allClaimants] = useState<Claimant[]>(MOCK_CLAIMANTS);
  const [claimants, setClaimants] = useState<Claimant[]>([]);
  const [total,     setTotal]     = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [filters,   setFiltersState] = useState<ClaimantFilters>({
    tortCode, page: 1, pageSize: 25,
  });

  // Sync tortCode prop changes into filters state (handles parent re-renders)
  useEffect(() => {
    setFiltersState(prev => ({ ...prev, tortCode }));
  }, [tortCode]);

  const setFilters = useCallback((f: Partial<ClaimantFilters>) => {
    setFiltersState(prev => ({ ...prev, ...f, page: 1 }));
  }, []);

  const fetchClaimants = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // TODO: replace with GET /claimants?tort_code=&page=&page_size=&search=
      await new Promise(res => setTimeout(res, 300));

      let results = [...allClaimants];

      // Apply tort filter
      if (filters.tortCode) {
        results = results.filter(c => c.tortCode === filters.tortCode);
      }

      // Apply search filter
      if (filters.searchQuery) {
        const q = filters.searchQuery.toLowerCase();
        results = results.filter(c =>
          (c.firstName + ' ' + c.lastName).toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q)
        );
      }

      // Apply pipeline stage filter
      if (filters.pipelineStage) {
        results = results.filter(c => c.pipelineStage === filters.pipelineStage);
      }

      setClaimants(results);
      setTotal(results.length);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load claimants');
    } finally {
      setIsLoading(false);
    }
  }, [filters, allClaimants]);

  // Auto-fetch on filter change
  useEffect(() => {
    fetchClaimants();
  }, [fetchClaimants]);

  const createClaimant = useCallback(async (data: Partial<Claimant>) => {
    // TODO: POST /claimants — handle 409 Conflict (F-08 hard block)
    console.warn('[useClaimants] createClaimant — TODO: wire to POST /claimants', data);
    return null;
  }, []);

  const updateClaimant = useCallback(async (id: string, data: Partial<Claimant>) => {
    // TODO: PUT /claimants/:id — CLIENT_USER cannot edit core fields (F-04 RBAC)
    console.warn('[useClaimants] updateClaimant — TODO:', id, data);
    return null;
  }, []);

  const advanceStage = useCallback(async (id: string, newStage: string) => {
    // TODO: PUT /claimants/:id/stage
    console.warn('[useClaimants] advanceStage — TODO:', id, newStage);
  }, []);

  const acknowledgeOverlap = useCallback(async (id: string) => {
    // TODO: PUT /claimants/:id — set cross_litigation_acknowledged fields (F-08)
    console.warn('[useClaimants] acknowledgeOverlap — TODO:', id);
  }, []);

  return {
    claimants, total, isLoading, error, filters,
    setFilters, fetchClaimants, createClaimant,
    updateClaimant, advanceStage, acknowledgeOverlap,
  };
}